import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { Brand } from 'src/app/models/brand.model';
import { BrandInductionConfig } from 'src/app/models/brand-induction-config.model';
import { InductionValidationResult } from 'src/app/models/induction-validation-result.model';
import { SiteInductionInfo, User } from 'src/app/models/user.model';
import { SiteInductionValidationService } from 'src/app/services/site-induction/site-induction-validation.service';
import { SiteApiService } from 'src/app/services/site/site.api.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalizationService } from '../internationalization/localization.service';
import { InductionFormGroupKeys, SiteInductionStatus } from 'src/app/shared/enums';

@Component({
    selector: 'app-site-induction-add',
    templateUrl: './site-induction-add-edit.component.html',
    styleUrls: ['./site-induction-add-edit.component.scss'],
    standalone: false
})
export class SiteInductionAddEditComponent implements OnInit {
  public inductionFormGroup! : UntypedFormGroup;
  siteInductionInfo : SiteInductionInfo | undefined;
  currentUser : User | undefined;
  brands : Brand[] = [];
  brandSelected : Brand | undefined;
  isEditMode : boolean = false;
  showConfirmation = false;
  validationResult: InductionValidationResult | undefined;
  isSaving = false;
  readonly InductionFormGroupKeys = InductionFormGroupKeys;
  readonly SiteInductionStatus = SiteInductionStatus;

  constructor(private _dialogRef: MatDialogRef<SiteInductionAddEditComponent>,
    private _localizationService : LocalizationService,
    private _formBuilder: UntypedFormBuilder,
    private _userService : UserService,
    private _siteApiService : SiteApiService,
    private _siteInductionValidationService: SiteInductionValidationService,
    @Inject(MAT_DIALOG_DATA) public data: any) {
      forkJoin({
        brands: this._siteApiService.getPartners(),
        configs: this._userService.getBrandInductionConfigs()
      }).subscribe({
        next: ({ brands, configs }) => {
          if (brands && configs) {
            const inductionBrandIds = new Set(configs
              .filter(c => c.brand?.isActive)
              .map(c => c.brand!.id));
            this.brands = brands.filter(b => inductionBrandIds.has(b.id));
          }
          this.setBrandSelectedFromEditMode();
        },
        error: () => {
          alert(this._localizationService.translate('register_getpartner_error'))
        }
      })
    }

  ngOnInit(): void {
    this.currentUser = this.data.user;
    this.siteInductionInfo = this.data.induction;
    this.isEditMode = this.data.isEditMode;
    this.inductionFormGroup = this.initFormGroup();
    this.setBrandSelectedFromEditMode();
  }

  selectedBrand(event : any){
    this.brandSelected = event;

    if (this.currentUser && this.brandSelected) {
      const exists = this.currentUser.siteInductionInfos.some(
        induction => induction.brandId.toLowerCase() === this.brandSelected!.id.toLowerCase()
      );
      if (exists) {
        this.inductionFormGroup.get(InductionFormGroupKeys.BRAND_NAME)?.setErrors({ 'brandExists': true });
      } else {
        const errors = this.inductionFormGroup.get(InductionFormGroupKeys.BRAND_NAME)?.errors;
        if (errors) {
          delete errors['brandExists'];
          const hasRemainingErrors = Object.keys(errors).length > 0;
          this.inductionFormGroup.get(InductionFormGroupKeys.BRAND_NAME)?.setErrors(hasRemainingErrors ? errors : null);
        }
      }
    }
  }

  public dismissDialog(): void {
    this._dialogRef.close();
  }

  getBrandImageUrl(): string | undefined {
    if (this.brandSelected?.imageUrl) {
      return this.brandSelected.imageUrl;
    }

    if (this.isEditMode && this.siteInductionInfo?.brandId) {
      return this.brands.find(b => b.id === this.siteInductionInfo?.brandId)?.imageUrl;
    }

    return undefined;
  }

  private initFormGroup(): UntypedFormGroup {
    const inductionForm = this._formBuilder.group({
      brandName: new UntypedFormControl(
      { value: this.siteInductionInfo?.brandName, disabled: this.isEditMode },
      this.isEditMode ? [] : [Validators.required]
    ),
      inductionId: new UntypedFormControl(this.siteInductionInfo?.inductionId, [Validators.required])
    });

    return inductionForm;
  }

  public hasError(form: UntypedFormGroup, controlName: string): boolean {
    const validationOutput = this.getError(form, controlName);
    return validationOutput !== '';
  }

  public getError(form: UntypedFormGroup, controlName: string): string {
    switch (controlName) {
      case InductionFormGroupKeys.BRAND_NAME:
        if (this.formHasError(form, controlName, 'required')) {
          return this._localizationService.translate('register_brandname_required');
        }
        if (this.formHasError(form, controlName, 'brandExists')) {
          return this._localizationService.translate('register_brandname_exists');
        }
        break;
      case InductionFormGroupKeys.INDUCTION_ID:  
        if (this.formHasError(form, controlName, 'required')) {
          return this._localizationService.translate('register_inductionid_required');
        } 
        if (this.formHasError(form, controlName, 'exists')) {
          return this._localizationService.translate('register_inductionid_exists');
        } 
        break;
    }
    return '';
  }

  private formHasError(form: UntypedFormGroup, controlName: string, errorName: string): boolean {
    return form.controls[controlName].hasError(errorName);
  }

  disableSubmitButton(){
    const hasUnsavedChanges = !this.isEditMode || this.inductionFormGroup.dirty;
    return !this.inductionFormGroup.valid || !hasUnsavedChanges || this.isSaving;
  }

  submitInduction(): void {
    if (!this.validateLocalForm()) {
      return;
    }

    const brand = this.getSelectedBrand();
    const inductionId = this.inductionFormGroup.get(InductionFormGroupKeys.INDUCTION_ID)?.value;

    if (!brand || !inductionId) {
      return;
    }

    this.isSaving = true;
    this._siteInductionValidationService.validate(brand.id, brand.name, inductionId).subscribe({
      next: (result) => {
        this.isSaving = false;
        this.validationResult = result;
        this.showConfirmation = true;
      },
      error: () => {
        this.isSaving = false;
        alert(this._localizationService.translate('register_siteinductionid_saveerror'));
      }
    });
  }

  onConfirmationContinue(): void {
    if (this.validationResult?.success) {
      const newInduction = this.validationResult.siteInduction;
      if (this.currentUser && newInduction) {
        if (this.isEditMode) {
          const idx = this.currentUser.siteInductionInfos.findIndex(
            i => i.id === this.siteInductionInfo?.id);
          if (idx !== -1) {
            this.currentUser.siteInductionInfos[idx] = newInduction;
            this.currentUser.siteInductionInfos = [...this.currentUser.siteInductionInfos];
          }
        } else {
          this.currentUser.siteInductionInfos = [...this.currentUser.siteInductionInfos, newInduction];
        }
      }
      this._dialogRef.close(this.currentUser);
      return;
    }

    this.dismissDialog();
  }

  onConfirmationResubmit(): void {
    this.showConfirmation = false;
    this.validationResult = undefined;
  }

  validateLocalForm() : boolean{
    const inductionId = this.inductionFormGroup.get(InductionFormGroupKeys.INDUCTION_ID);
    if (!this.isEditMode && this.brandSelected && this.currentUser){
      if (inductionId?.value){
        if (this.currentUser.siteInductionInfos.some(induction => induction.inductionId.toLowerCase() == inductionId.value.toLowerCase() && 
                                                                  induction.brandId.toLowerCase() == this.brandSelected?.id.toLowerCase())){
          inductionId.setErrors({ 'exists': true });
          return false;
        }
      }
    }
    return true;
  }

  getSelectedBrandName(): string {
    return this.getSelectedBrand()?.name ?? '';
  }

  private getSelectedBrand(): Brand | undefined {
    if (this.brandSelected) {
      return this.brandSelected;
    }

    if (this.isEditMode && this.siteInductionInfo) {
      return {
        id: this.siteInductionInfo.brandId,
        code: '',
        name: this.siteInductionInfo.brandName,
        imageUrl: this.getBrandImageUrl() ?? '',
        isActive: true,
        sites: []
      };
    }

    return undefined;
  }

  private setBrandSelectedFromEditMode(): void {
    if (!this.isEditMode || !this.siteInductionInfo || this.brandSelected) {
      return;
    }

    const matchedBrand = this.brands.find(b => b.id === this.siteInductionInfo?.brandId);
    if (matchedBrand) {
      this.brandSelected = matchedBrand;
      return;
    }

    this.brandSelected = {
      id: this.siteInductionInfo.brandId,
      code: '',
      name: this.siteInductionInfo.brandName,
      imageUrl: '',
      isActive: true,
      sites: []
    };
  }

}